import { getStoredToken } from '../context/AuthContext';
import type { AuthUser } from '../context/AuthContext';

const BASE = '/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? res.statusText);
  }

  return res.json() as Promise<T>;
}

// ── Auth ───────────────────────────────────────────────────────────────────

export interface LoginResponse {
  token: string;
  expiresIn: number;
  user: AuthUser;
}

export function login(login: string, password: string) {
  return request<LoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ login, password }),
  });
}

export function register(email: string, password: string, username?: string) {
  return request<{ user: AuthUser }>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, username }),
  });
}

export function logout() {
  return request<{ ok: boolean }>('/auth/logout', { method: 'POST' });
}

export function getMe() {
  return request<AuthUser>('/auth/me');
}

// ── Inventory ──────────────────────────────────────────────────────────────

export function listInventory(params: {
  search?: string;
  type?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.type)   qs.set('type', params.type);
  if (params.page)   qs.set('page', String(params.page));
  if (params.limit)  qs.set('limit', String(params.limit));
  return request<import('../types').InventoryListResponse>(`/inventory?${qs}`);
}

export function getItem(id: number) {
  return request<import('../types').InventoryItem>(`/inventory/${id}`);
}

export function createItem(data: Partial<import('../types').InventoryItem>) {
  return request<import('../types').InventoryItem>('/inventory', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateItem(id: number, data: Partial<import('../types').InventoryItem>) {
  return request<import('../types').InventoryItem>(`/inventory/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteItem(id: number) {
  return request<{ success: boolean }>(`/inventory/${id}`, { method: 'DELETE' });
}

export function receiveItem(id: number, qty: number, notes?: string) {
  return request<{ id: number; qty: number }>(`/inventory/${id}/receive`, {
    method: 'POST',
    body: JSON.stringify({ qty, notes }),
  });
}

export function issueItem(id: number, qty: number, notes?: string) {
  return request<{ id: number; qty: number }>(`/inventory/${id}/issue`, {
    method: 'POST',
    body: JSON.stringify({ qty, notes }),
  });
}

export function getItemTransactions(id: number, page = 1) {
  return request<{ transactions: import('../types').Transaction[]; total: number }>(
    `/inventory/${id}/transactions?page=${page}`
  );
}

// ── Scanner ────────────────────────────────────────────────────────────────

export function scanLookup(code: string) {
  return request<import('../types').InventoryItem>(`/scanner/${encodeURIComponent(code)}`);
}

// ── Settings ───────────────────────────────────────────────────────────────

export function listItemTypes()    { return request<import('../types').ItemType[]>('/settings/item-types'); }
export function listManufacturers(){ return request<import('../types').Manufacturer[]>('/settings/manufacturers'); }
export function listVendors()      { return request<import('../types').Vendor[]>('/settings/vendors'); }
export function listWarehouses()   { return request<import('../types').Warehouse[]>('/settings/warehouses'); }

function settingsCrud<T>(base: string) {
  return {
    create: (data: Partial<T>) =>
      request<T>(`/settings/${base}`, { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: Partial<T>) =>
      request<T>(`/settings/${base}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    remove: (id: number) =>
      request<{ success: boolean }>(`/settings/${base}/${id}`, { method: 'DELETE' }),
  };
}

export const itemTypesApi    = settingsCrud<import('../types').ItemType>('item-types');
export const manufacturersApi = settingsCrud<import('../types').Manufacturer>('manufacturers');
export const vendorsApi      = settingsCrud<import('../types').Vendor>('vendors');
export const warehousesApi   = settingsCrud<import('../types').Warehouse>('warehouses');

// ── Reports ────────────────────────────────────────────────────────────────

export function getReportSummary() {
  return request<import('../types').ReportSummary>('/reports/summary');
}

export function getTransactionReport(params: {
  start?: string; end?: string; type?: string; user?: string; page?: number;
}) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]);
  return request<{ transactions: import('../types').Transaction[]; total: number; page: number; limit: number }>(
    `/reports/transactions?${qs}`
  );
}

export function getLowStockReport(threshold?: number) {
  const qs = threshold != null ? `?threshold=${threshold}` : '';
  return request<import('../types').InventoryItem[]>(`/reports/low-stock${qs}`);
}

export function getValueByTypeReport() {
  return request<{ item_type: string; item_count: number; total_qty: number; total_value: number }[]>(
    '/reports/value-by-type'
  );
}

// ── Users ──────────────────────────────────────────────────────────────────

export function listUsers() { return request<import('../types').User[]>('/users'); }

export function createUser(data: { email: string; username?: string; password: string; role: string }) {
  return request<import('../types').User>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateUser(id: number, data: { role?: string; password?: string; username?: string }) {
  return request<import('../types').User>(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteUser(id: number) {
  return request<{ ok: boolean }>(`/users/${id}`, { method: 'DELETE' });
}

// ── Images ─────────────────────────────────────────────────────────────────

export async function uploadImage(file: File): Promise<{ key: string; url: string }> {
  const token = getStoredToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/images/upload`, {
    method: 'POST',
    body: form,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Upload failed');
  return res.json();
}
